import { Component, input, signal, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { IconComponent } from '../../icon/icon.component';

export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'htx-dropdown-field',
  imports: [ReactiveFormsModule, NgClass, IconComponent],
  templateUrl: './dropdown-field.component.html',
  styleUrl: './dropdown-field.component.scss'
})
export class DropdownFieldComponent implements OnInit {
  private uniqueId = Math.random().toString(36).substring(2, 11);

  /** FormControl instance for managing the dropdown's value and validation state */
  ctrl = input.required<AbstractControl>();

  /** Label text displayed above the dropdown field */
  label = input<string>();

  /** Whether to display a required indicator (*) next to the label */
  required = input<boolean>(false);

  /** Array of dropdown options */
  options = input.required<DropdownOption[]>();

  /** Placeholder text when no option is selected */
  placeholder = input<string>('Select an option');

  /**
   * When true, multiple options can be selected. The bound control then holds an
   * array of the selected values, the menu stays open while selecting, and
   * selected options are marked with a check. Defaults to single-select.
   */
  multiple = input<boolean>(false);

  isOpen = signal<boolean>(false);

  ngOnInit(): void {
    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
      const element = event.target as HTMLElement;
      if (!element.closest(`[data-id="${this.getUniqueId()}"]`)) {
        this.isOpen.set(false);
      }
    });
  }

  getControl(): FormControl {
    return this.ctrl() as FormControl;
  }

  getUniqueId(): string {
    return this.uniqueId;
  }

  /** Currently selected values when in multiple mode (empty array otherwise). */
  private selectedValues(): (string | number)[] {
    const value = this.getControl().value;
    return Array.isArray(value) ? value : [];
  }

  /** Whether the field currently holds a value (drives the floating label / placeholder). */
  hasValue(): boolean {
    if (this.multiple()) {
      return this.selectedValues().length > 0;
    }
    const value = this.getControl().value;
    return value !== null && value !== undefined && value !== '';
  }

  isSelected(option: DropdownOption): boolean {
    return this.multiple() && this.selectedValues().includes(option.value);
  }

  getSelectedLabel(): string {
    if (this.multiple()) {
      const selected = this.selectedValues();
      if (selected.length === 0) {
        return this.placeholder();
      }
      return this.options()
        .filter((opt) => selected.includes(opt.value))
        .map((opt) => opt.label)
        .join(', ');
    }

    const selectedValue = this.getControl().value;
    if (!selectedValue) return this.placeholder();

    const option = this.options().find(opt => opt.value === selectedValue);
    return option ? option.label : this.placeholder();
  }

  toggleDropdown(): void {
    if (!this.getControl().disabled) {
      this.isOpen.set(!this.isOpen());
    }
  }

  selectOption(option: DropdownOption): void {
    if (option.disabled) {
      return;
    }

    if (this.multiple()) {
      const current = [...this.selectedValues()];
      const index = current.indexOf(option.value);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(option.value);
      }
      this.getControl().setValue(current);
      this.getControl().markAsDirty();
      // Keep the menu open so several options can be picked in one go.
      return;
    }

    this.getControl().setValue(option.value);
    this.isOpen.set(false);
  }
}
